import { Injectable, inject } from '@angular/core';
import { IConfigPais } from 'modelos/src';
import { HttpApiAdminService } from './http-api.service';
import { AuthService } from 'src/app/entidades/login/auth.service';
import { Observable, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaisConfigService {
  private httpApi = inject(HttpApiAdminService);
  private authService = inject(AuthService);

  private configCache: IConfigPais | null = null;

  /**
   * Obtiene la configuración de país del cliente actual
   * Usa cache para evitar múltiples llamadas a la API
   */
  public getConfigPais(): Observable<IConfigPais> {
    return this.httpApi.get('/pais-config');
  }

  /**
   * Obtiene la configuración de país y la cachea localmente
   */
  public async getConfigPaisAsync(): Promise<IConfigPais> {
    if (this.configCache) {
      return this.configCache;
    }

    const config = await firstValueFrom(this.getConfigPais());
    if (config) {
      this.configCache = config;
    }
    return config!;
  }

  /**
   * Limpia el cache (útil al cambiar de cliente)
   */
  public clearCache(): void {
    this.configCache = null;
  }

  /**
   * Obtiene el regex de validación de teléfono según el país del cliente
   * Retorna el patrón de Argentina por defecto si no se encuentra configuración
   */
  public async getTelefonoPattern(): Promise<string> {
    const config = await this.getConfigPaisAsync();
    return config.validaciones?.telefono || '^[1-9][0-9]{9}$';
  }

  /**
   * Obtiene el regex de validación de DNI según el país del cliente
   * Retorna el patrón de Argentina por defecto si no se encuentra configuración
   */
  public async getDniPattern(): Promise<string> {
    const config = await this.getConfigPaisAsync();
    return config.validaciones?.dni || '^[0-9]{7,8}$';
  }

  /**
   * Obtiene el código de país (ej: '+54', '+51', '+52')
   */
  public async getCodigoPais(): Promise<string> {
    const config = await this.getConfigPaisAsync();
    return config.codigoPais;
  }

  /**
   * Obtiene la estructura administrativa (nombres de niveles)
   */
  public async getEstructuraAdministrativa(): Promise<{
    nivel1: string;
    nivel2: string;
    nivel3: string;
  }> {
    const config = await this.getConfigPaisAsync();
    return (
      config.estructuraAdministrativa || {
        nivel1: 'Nivel 1',
        nivel2: 'Nivel 2',
        nivel3: 'Nivel 3',
      }
    );
  }
}
